/**
 * The company's legal identity, as published in the Impressum.
 *
 * These are facts about the business, not copy: the legal name, the address and the
 * registry numbers read the same in every language, so they live here once instead of
 * in five locale files where they would drift. Only the LABELS around them are
 * translated (legal:impressum.*).
 *
 * APPLICABLE LAW IS ESTONIAN, not Austrian or German. Studio RRB OÜ is an Estonian
 * osaühing, so the disclosure follows the Äriseadustik §15¹ (name, registry code and
 * seat on business documents and the website) and the Infoühiskonna teenuse seadus §4,
 * Estonia's implementation of the e-Commerce Directive: name, seat, contact allowing
 * rapid and direct communication, registry code, and VAT number where registered.
 *
 * Under the country-of-origin principle of Directive 2000/31/EC an EU provider follows
 * its home state's rules when serving customers elsewhere in the Union, so selling into
 * Austria and Germany does not pull in §5 ECG, the Gewerbeordnung, Firmenbuch or
 * Wirtschaftskammer. The page keeps the name "Impressum" because that is what an
 * Austrian or German visitor looks for in the footer.
 */
export interface CompanyDetails {
  /** Registered name including legal form. */
  legalName: string;
  /** Board member(s) — juhatuse liige. Optional under Estonian law; empty hides the row. */
  representative: string;
  street: string;
  postalCode: string;
  city: string;
  /** Estonian county (maakond). */
  county: string;
  country: string;
  email: string;
  /** Optional: email alone satisfies the "rapid and direct" requirement. */
  phone: string;
  /** Estonian commercial register code (registrikood). */
  registryCode: string;
  /** VAT number (KMKR number), empty if not VAT registered. */
  vatId: string;
  /** Where the company is registered, for the applicable-law line. */
  jurisdiction: string;
}

export const COMPANY: CompanyDetails = {
  legalName: 'Studio RRB OÜ',
  representative: '',
  street: 'Sepapaja tn 6',
  postalCode: '15551',
  city: 'Tallinn',
  county: 'Harju maakond',
  country: 'Estonia',
  email: 'legal@slotyapp.com',
  phone: '',
  registryCode: '16362886',
  vatId: 'EE102434956',
  jurisdiction: 'Estonia',
};

/** True while any required value is still a scaffolded placeholder. */
export const hasPlaceholders = (c: CompanyDetails = COMPANY): boolean =>
  Object.values(c).some((v) => v.startsWith('[') && v.endsWith(']'));

/** The Estonian commercial register, where the registry code above can be checked. */
export const BUSINESS_REGISTER_URL = 'https://ariregister.rik.ee/eng';
